import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type { AgentRecord } from 'src/app/core/services/agent-api.service';
import { AppDatePipe } from 'src/app/shared/pipes/app-date.pipe';
import { ActionMenuPanelDirective } from 'src/app/shared/directives/action-menu-panel.directive';
import { agentDetailPath } from '../agent-route.util';
import {
  agentLifecycleAction,
  agentStatusDisplayLabel,
  lifecycleMenuLabel,
  statusBadgeClassForAgent,
} from '../agent-lifecycle.util';

@Component({
  selector: 'app-manage-agent-table',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IconModule,
    HasPermissionPipe,
    AppDatePipe,
    ListModuleTableComponent,
    ListCellDefDirective,
    ActionMenuPanelDirective,
  ],
  templateUrl: './manage-agent-table.component.html',
})
export class ManageAgentTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: AgentRecord[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No agents found.';
  @Input() openActionMenuKey: string | null = null;
  @Input() menuKeyPrefix: 'published' | 'draft' | 'deleted' = 'published';
  @Input() serialOffset = 0;

  @Output() toggleMenu = new EventEmitter<{ key: string; event: MouseEvent }>();
  @Output() closeMenu = new EventEmitter<void>();
  @Output() viewDetail = new EventEmitter<AgentRecord>();
  @Output() retrain = new EventEmitter<AgentRecord>();
  @Output() assign = new EventEmitter<AgentRecord>();
  @Output() version = new EventEmitter<AgentRecord>();
  @Output() pauseAgent = new EventEmitter<AgentRecord>();
  @Output() delete = new EventEmitter<AgentRecord>();
  @Output() restore = new EventEmitter<AgentRecord>();
  @Output() permanentDelete = new EventEmitter<AgentRecord>();

  get isDeletedTab(): boolean {
    return this.menuKeyPrefix === 'deleted';
  }

  trackAgentRow = (row: unknown): string => {
    const a = row as AgentRecord;
    return `${a.id}-${a.status ?? ''}`;
  };

  get columns(): ListModuleColumnDef[] {
    return [
      { id: 'serial', header: 'Sr No', cellClass: 'whitespace-nowrap' },
      { id: 'agentId', header: 'Agent ID', cellClass: 'font-mono text-xs' },
      { id: 'agentName', header: 'Agent Name' },
      { id: 'client', header: 'Client' },
      { id: 'product', header: 'Product' },
      { id: 'workflow', header: 'Workflow', cellClass: 'font-mono text-xs' },
      { id: 'trainedOn', header: 'Trained On' },
      { id: 'status', header: 'Status', align: 'center', cellClass: 'whitespace-nowrap text-xs font-bold' },
      { id: 'totalPds', header: 'Total PDs', align: 'right', cellClass: 'text-end' },
      { id: 'actions', header: 'Action', align: 'right', cellClass: 'text-end' },
    ];
  }

  actionMenuRowKey(index: number): string {
    return `${this.menuKeyPrefix}-${index}`;
  }

  serialNumber(index: number): number {
    return this.serialOffset + index + 1;
  }

  agentDetailRoute(row: AgentRecord): string[] | null {
    return agentDetailPath(row);
  }

  statusBadgeClass(status: AgentRecord['status']): Record<string, boolean> {
    return statusBadgeClassForAgent(status);
  }

  statusLabel(status: AgentRecord['status']): string {
    return agentStatusDisplayLabel(status);
  }

  canToggleLifecycle(row: AgentRecord): boolean {
    return agentLifecycleAction(row.status) != null;
  }

  lifecycleLabel(row: AgentRecord): string {
    return lifecycleMenuLabel(row.status);
  }

  isPauseMenuAction(row: AgentRecord): boolean {
    return agentLifecycleAction(row.status) === 'pause';
  }

  isGoLiveMenuAction(row: AgentRecord): boolean {
    return agentLifecycleAction(row.status) === 'activate' && String(row.status ?? '').toUpperCase() === 'DEPLOYED';
  }

  isResumeMenuAction(row: AgentRecord): boolean {
    return agentLifecycleAction(row.status) === 'activate' && String(row.status ?? '').toUpperCase() === 'INACTIVE';
  }
}
